import { set } from 'es-toolkit/compat';
import { encodeAssetId, uploadFileToS3 } from 'features/assets/assets.util';
import { type BulkCreateAssetInput, type BulkCreateAssetMutationResponse, useBulkCreateAssetMutation } from 'generated/graphql';
import { useCallback, useMemo, useState } from 'react';
import { type AssetUploadMeta, getBulkUploadAssetsMetaArray } from './asset-extraction-utils';

/**
 * Result of uploading a single asset to S3
 *
 * @typedef {Object} UploadAssetResult
 * @property {AssetUploadMeta} assetMeta - The metadata for the uploaded asset
 * @property {string} [assetId] - The ID of the uploaded asset if upload was successful
 * @property {boolean} uploadSuccess - Whether the upload was successful
 * @property {string} [errorMessage] - Error message if upload failed
 */
type UploadAssetResult = {
  assetMeta: AssetUploadMeta;
  assetId?: string;
  uploadSuccess: boolean;
  errorMessage?: string;
};

/**
 * Status of an asset upload process
 */
export type UploadStatus = 'PENDING' | 'CREATING' | 'UPLOADING' | 'SUCCESS' | 'ERROR';

/**
 * Map of upload statuses to display text
 */
const statusToDisplayMap: Record<UploadStatus, string> = {
  CREATING: 'Creating upload URL',
  ERROR: 'Error :(',
  PENDING: 'Pending...',
  SUCCESS: 'Upload Success',
  UPLOADING: 'Uploading...',
};

/**
 * Display data for an asset being uploaded
 *
 * @typedef {Object} AssetDisplayData
 * @property {string} uploadTrackingId - Unique identifier for tracking this upload
 * @property {string} [localPreviewUrl] - Base64 URL for preview display
 * @property {Object} dimensions - Image dimensions
 * @property {number} dimensions.width - Width of the image in pixels
 * @property {number} dimensions.height - Height of the image in pixels
 * @property {string} fileName - Original file name of the asset
 * @property {string} [errorMessage] - Error message if upload failed
 * @property {number} uploadProgressPercentage - Current upload progress (0-100)
 * @property {UploadStatus} uploadStatus - Current status of the upload
 * @property {string} uploadStatusText - Human-readable status text
 * @property {string[]} contentPaths - Paths in the content object where this asset is used
 */
export type AssetDisplayData = {
  uploadTrackingId: string;
  localPreviewUrl?: string;
  dimensions: {
    width: number;
    height: number;
  };
  fileName: string;
  errorMessage?: string;
  uploadProgressPercentage: number;
  uploadStatus: UploadStatus;
  uploadStatusText: string;
  contentPaths: string[];
};

/**
 * Function signature for extracting and uploading embedded assets from content
 *
 * @typedef {Function} ExtractAndUploadEmbeddedAssetsFn
 * @param {Object} params - Function parameters
 * @param {Record<string, unknown>} params.someContent - Content object that may contain embedded assets
 * @param {string} params.projectId - ID of the project to associate with uploaded assets
 * @returns {Promise<Object>} - Promise resolving to either success or failure result
 * @returns {Record<string, unknown>} [return.newContent] - Content with embedded assets replaced with asset IDs
 * @returns {boolean} return.success - Whether the operation was successful
 * @returns {string} [return.errorMessage] - Error message if operation failed
 */
type ExtractAndUploadEmbeddedAssetsFn = ({
  someContent,
  projectId,
}: {
  someContent: Record<string, unknown>;
  projectId: string;
}) => Promise<
  | {
      newContent: Record<string, unknown>;
      success: true;
      errorMessage?: undefined;
    }
  | {
      newContent?: undefined;
      success: false;
      errorMessage: string;
    }
>;

/**
 * Hook for extracting and uploading embedded assets (like base64 images) from content objects
 *
 * This hook provides functionality to:
 * 1. Scan content objects for embedded assets
 * 2. Create asset records in the backend
 * 3. Upload assets to S3
 * 4. Replace embedded assets in content with asset IDs
 * 5. Track upload progress and status
 *
 * @returns {Object} Hook result object
 * @returns {AssetDisplayData[]} returns.assetDisplayDataArray - Array of asset data for display
 * @returns {ExtractAndUploadEmbeddedAssetsFn} returns.extractAndUploadEmbeddedAssets - Function to extract and upload assets
 * @returns {boolean} returns.isUploading - Whether assets are currently being uploaded
 */
export const useExtractAndUploadEmbeddedAssets = (): {
  assetDisplayDataArray: AssetDisplayData[];
  extractAndUploadEmbeddedAssets: ExtractAndUploadEmbeddedAssetsFn;
  isUploading: boolean;
} => {
  const [bulkCreateAssets] = useBulkCreateAssetMutation();
  const [uploadStatusMap, setUploadStatusMap] = useState<Map<string, UploadStatus>>(new Map());
  const [uploadErrorMap, setUploadErrorMap] = useState<Map<string, string>>(new Map());
  const [uploadProgressMap, setUploadProgressMap] = useState<Map<string, number>>(new Map());
  const [assetsMetaCopy, setAssetsMetaCopy] = useState<AssetUploadMeta[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Handles the actual upload of files to S3 using signed URLs from the backend
   *
   * @param {AssetUploadMeta[]} assetsMetaArray - Array of asset metadata objects
   * @param {BulkCreateAssetMutationResponse[]} bulkCreateAssetData - Asset creation response data from backend
   * @returns {Promise<UploadAssetResult[]>} Results of all uploads
   */
  const handleUploadFilesToS3 = useCallback(
    async (assetsMetaArray: AssetUploadMeta[], bulkCreateAssetData: BulkCreateAssetMutationResponse[]): Promise<UploadAssetResult[]> => {
      if (!assetsMetaArray.length) {
        return [];
      }

      setUploadStatusMap(
        new Map(
          assetsMetaArray.map((asset) => [
            asset.uploadTrackingId,
            'UPLOADING',
          ]),
        ),
      );
      setUploadErrorMap(new Map());
      setUploadProgressMap(
        new Map(
          assetsMetaArray.map(({ uploadTrackingId }) => [
            uploadTrackingId,
            0,
          ]),
        ),
      );
      const uploadPromises: Promise<UploadAssetResult>[] = assetsMetaArray.map(async (assetMeta) => {
        try {
          const matchingCreateAssetResponse = bulkCreateAssetData.find((asset) => asset && asset.id === assetMeta.uploadTrackingId);
          if (!matchingCreateAssetResponse || !matchingCreateAssetResponse?.success || !matchingCreateAssetResponse.asset.s3SignedUrlPut) {
            throw new Error('No place to upload asset');
          }

          const uploadObservable = uploadFileToS3({
            file: assetMeta.file,
            signedUrl: matchingCreateAssetResponse.asset.s3SignedUrlPut,
          });

          await new Promise<void>((resolve, reject) => {
            uploadObservable.subscribe({
              complete: () => resolve(),
              error: (error) => reject(error),
              next: (progress) => {
                let newProgressPercentage = 0;
                if (progress.total !== undefined && progress.loaded !== undefined && progress.total > 0) {
                  // Only update percentage if we have defined numbers
                  newProgressPercentage = (progress.loaded / progress.total) * 100;
                }
                if (progress.done) {
                  newProgressPercentage = 100;
                }
                setUploadProgressMap((currentMap) => {
                  const newMap = new Map(currentMap);
                  newMap.set(assetMeta.uploadTrackingId, newProgressPercentage);
                  return newMap;
                });
              },
            });
          });

          setUploadStatusMap((currentMap) => {
            const newMap = new Map(currentMap);
            newMap.set(assetMeta.uploadTrackingId, 'SUCCESS');
            return newMap;
          });

          return {
            assetId: matchingCreateAssetResponse.asset.assetId,
            assetMeta,
            errorMessage: undefined,
            uploadSuccess: true,
          };
        } catch (e) {
          const errorMessage = `Could not upload ${assetMeta.file.name} to S3: ${e instanceof Error ? e.message : 'An unknown error occurred'}`;
          console.error(errorMessage);
          setUploadErrorMap((currentMap) => {
            const newMap = new Map(currentMap);
            newMap.set(assetMeta.uploadTrackingId, errorMessage);
            return newMap;
          });
          setUploadStatusMap((currentMap) => {
            const newMap = new Map(currentMap);
            newMap.set(assetMeta.uploadTrackingId, 'ERROR');
            return newMap;
          });
          return {
            assetMeta,
            errorMessage,
            uploadSuccess: false,
          };
        }
      });

      return Promise.all(uploadPromises);
    },
    [],
  );

  /**
   * Main function to extract embedded assets from content and upload them
   *
   * @param {Object} params - Function parameters
   * @param {Record<string, unknown>} params.someContent - Content object that may contain embedded assets
   * @param {string} params.projectId - ID of the project to associate with uploaded assets
   * @returns {Promise<Object>} Result object with success status and new content
   */
  const extractAndUploadEmbeddedAssets: ExtractAndUploadEmbeddedAssetsFn = useCallback(
    async ({ someContent, projectId }) => {
      try {
        if (!projectId) {
          throw new Error('No project id found.');
        }
        setIsUploading(true);
        setAssetsMetaCopy([]);
        setUploadStatusMap(new Map());
        setUploadErrorMap(new Map());
        setUploadProgressMap(new Map());

        const assetsMetaArray = await getBulkUploadAssetsMetaArray({
          projectId,
          someContent,
        });
        if (!assetsMetaArray.length) {
          // no assets found, so we just return the content
          return {
            newContent: someContent,
            success: true,
          };
        }

        setAssetsMetaCopy(assetsMetaArray);
        setUploadStatusMap(
          new Map(
            assetsMetaArray.map((asset) => [
              asset.uploadTrackingId,
              'CREATING',
            ]),
          ),
        );
        setUploadProgressMap(
          new Map(
            assetsMetaArray.map(({ uploadTrackingId }) => [
              uploadTrackingId,
              0,
            ]),
          ),
        );

        const bulkCreateAssetsInput: BulkCreateAssetInput[] = assetsMetaArray.map(
          ({ createAssetMutationInput }) => createAssetMutationInput,
        );

        const bulkCreateAssetsResult = await bulkCreateAssets({
          variables: {
            input: bulkCreateAssetsInput,
          },
        });

        if (bulkCreateAssetsResult.errors) {
          console.error(bulkCreateAssetsResult.errors);
          throw new Error('Error creating assets');
        }
        const bulkCreateAssetData = bulkCreateAssetsResult.data?.bulkCreateAsset;
        if (!bulkCreateAssetData || bulkCreateAssetData.length === 0) {
          console.error('no bulkCreateAsset data returned');
          throw new Error('Error creating assets');
        }

        if (bulkCreateAssetData.length !== assetsMetaArray.length) {
          throw new Error('BulkCreateAssets length does not equal number of assets in content');
        }

        setAssetsMetaCopy(assetsMetaArray);
        const uploadAssetsResult = await handleUploadFilesToS3(assetsMetaArray, bulkCreateAssetData);

        const newContent = {
          ...someContent,
        };

        uploadAssetsResult.forEach((uploadResult) => {
          if (!uploadResult.assetId) {
            return;
          }
          const newAssetSrc = encodeAssetId(uploadResult.assetId);
          uploadResult.assetMeta.contentPaths.forEach((contentPath) => {
            set(newContent, contentPath, newAssetSrc);
          });
        });

        return {
          newContent,
          success: true,
        };
      } catch (someError) {
        console.error(someError);
        const errorMessage = someError instanceof Error ? someError.message : 'Unknown error occurred when uploading assets';

        // Return the original content if there was an error
        return {
          errorMessage,
          newContent: undefined,
          success: false,
        };
      } finally {
        setIsUploading(false);
      }
    },
    [
      bulkCreateAssets,
      handleUploadFilesToS3,
    ],
  );

  /**
   * Transforms the internal asset metadata into display-ready data
   */
  const assetDisplayDataArray: AssetDisplayData[] = useMemo(
    () =>
      assetsMetaCopy.map((meta): AssetDisplayData => {
        const uploadStatus = uploadStatusMap.get(meta.uploadTrackingId) ?? 'PENDING';
        const uploadProgress = uploadProgressMap.get(meta.uploadTrackingId) ?? 0;
        const errorMessage = uploadErrorMap.get(meta.uploadTrackingId) ?? undefined;
        return {
          contentPaths: meta.contentPaths,
          dimensions: meta.dimensions,
          errorMessage,
          fileName: meta.file.name,
          localPreviewUrl: meta.base64AssetString,
          uploadProgressPercentage: uploadProgress,
          uploadStatus,
          uploadStatusText: statusToDisplayMap[uploadStatus],
          uploadTrackingId: meta.uploadTrackingId,
        };
      }),
    [
      assetsMetaCopy,
      uploadErrorMap,
      uploadProgressMap,
      uploadStatusMap,
    ],
  );

  return {
    assetDisplayDataArray,
    extractAndUploadEmbeddedAssets,
    isUploading,
  };
};
